// ============================================
// AlpenScreen Content Studio - Background Gallery Modal
// File: frontend/src/components/ContentStudio/BackgroundGalleryModal.js
// ✅ UNSPLASH PRODUCTION READY - Full Attribution Compliance
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Search,
  Image,
  Loader,
  ChevronLeft,
  ChevronRight,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const BackgroundGalleryModal = ({ onSelect, onClose }) => {
  const { t } = useTranslation();
  
  // State
  const [activeTab, setActiveTab] = useState('gallery'); // 'gallery' | 'unsplash'
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [unsplashImages, setUnsplashImages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [unsplashEnabled, setUnsplashEnabled] = useState(false);
  const [unsplashPage, setUnsplashPage] = useState(1);
  const [unsplashTotalPages, setUnsplashTotalPages] = useState(1);

  // Check status and load categories on mount
  useEffect(() => {
    checkStatus();
    loadCategories();
  }, []);

  // Load gallery when category changes
  useEffect(() => {
    loadGalleryImages();
  }, [selectedCategory]);

  // Check if Unsplash is enabled
  const checkStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/backgrounds/status`);
      const data = await response.json();
      setUnsplashEnabled(data.unsplashEnabled);
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  // Load categories
  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/backgrounds/categories`, {
        headers: { 'Accept-Language': 'de' }
      });
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Load local gallery images
  const loadGalleryImages = async () => {
    setLoading(true);
    try {
      const url = selectedCategory 
        ? `${API_URL}/backgrounds/gallery?category=${selectedCategory}`
        : `${API_URL}/backgrounds/gallery`;
      
      const response = await fetch(url);
      const data = await response.json();
      setGalleryImages(data.images || []);
    } catch (error) {
      console.error('Error loading gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search Unsplash
  const searchUnsplash = useCallback(async (query, page = 1) => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/backgrounds/unsplash/search?query=${encodeURIComponent(query)}&page=${page}&per_page=12`
      );
      const data = await response.json();
      
      if (page === 1) {
        setUnsplashImages(data.images || []);
      } else {
        setUnsplashImages(prev => [...prev, ...(data.images || [])]);
      }
      setUnsplashPage(data.page || 1);
      setUnsplashTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Error searching Unsplash:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setUnsplashPage(1);
      searchUnsplash(searchQuery, 1);
    }
  };

  // Load more Unsplash results
  const loadMoreUnsplash = () => {
    if (unsplashPage < unsplashTotalPages) {
      searchUnsplash(searchQuery, unsplashPage + 1);
    }
  };

  // ✅ Handle image selection with proper Unsplash tracking
const handleSelectImage = async (image, event) => {  // <-- event hinzufügen
  // Stop propagation
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }
  
  console.log('🖼️ Modal: handleSelectImage called');
  
  // Track Unsplash download
  if (image.source === 'unsplash' && image.downloadUrl) {
    try {
      await fetch(`${API_URL}/backgrounds/unsplash/track-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ downloadUrl: image.downloadUrl })
      });
    } catch (error) {
      console.error('Error tracking download:', error);
    }
  }
  
  const imageUrl = image.source === 'local' 
    ? `${API_URL.replace('/api', '')}${image.url}`
    : image.url;
  
  console.log('📞 Modal: Calling onSelect with:', imageUrl);
  
  onSelect(imageUrl, image);
};

  // Quick search suggestions for Unsplash
  const searchSuggestions = [
    { label: t('backgrounds.suggestions.restaurant', 'Restaurant'), query: 'restaurant interior' },
    { label: t('backgrounds.suggestions.fitness', 'Fitness'), query: 'gym fitness' },
    { label: t('backgrounds.suggestions.nature', 'Natur'), query: 'nature landscape' },
    { label: t('backgrounds.suggestions.office', 'Büro'), query: 'modern office' },
    { label: t('backgrounds.suggestions.abstract', 'Abstrakt'), query: 'abstract background' },
    { label: t('backgrounds.suggestions.texture', 'Textur'), query: 'texture pattern' }
  ];

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('backgrounds.title', 'Hintergrundbild auswählen')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('gallery')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'gallery'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Image size={18} className="inline mr-2" />
            {t('backgrounds.gallery', 'Galerie')}
          </button>
          {unsplashEnabled && (
            <button
              onClick={() => setActiveTab('unsplash')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'unsplash'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Search size={18} className="inline mr-2" />
              {t('backgrounds.unsplash', 'Unsplash Suche')}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          
          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <>
              {/* Category Filter */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      !selectedCategory
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('backgrounds.all', 'Alle')}
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        selectedCategory === cat.id
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {cat.icon} {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Gallery Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {loading ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader className="animate-spin text-purple-600" size={32} />
                  </div>
                ) : galleryImages.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {galleryImages.map(image => (
                      <button
                        key={image.id}
                        onClick={(e) => handleSelectImage(image, e)} 
                        className="aspect-video rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-all group relative"
                      >
                        <img
                          src={`${API_URL.replace('/api', '')}${image.thumb}`}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                    <Image size={48} className="mb-2 opacity-50" />
                    <p>{t('backgrounds.noImages', 'Keine Bilder in dieser Kategorie')}</p>
                    <p className="text-sm mt-1">{t('backgrounds.addImagesHint', 'Bilder können vom Admin hinzugefügt werden')}</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Unsplash Tab */}
          {activeTab === 'unsplash' && unsplashEnabled && (
            <>
              {/* Search Bar */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('backgrounds.searchPlaceholder', 'Nach Bildern suchen...')}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {t('common.search', 'Suchen')}
                  </button>
                </form>

                {/* Quick Suggestions */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {searchSuggestions.map(suggestion => (
                    <button
                      key={suggestion.query}
                      onClick={() => {
                        setSearchQuery(suggestion.query);
                        searchUnsplash(suggestion.query, 1);
                      }}
                      className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Unsplash Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {loading && unsplashImages.length === 0 ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader className="animate-spin text-purple-600" size={32} />
                  </div>
                ) : unsplashImages.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {unsplashImages.map(image => (
                        <button
                          key={image.id}
                          onClick={(e) => handleSelectImage(image, e)} 
                          className="aspect-video rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-all group relative"
                        >
                          <img
                            src={image.thumb}
                            alt={`Photo by ${image.author}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          
                          {/* ✅ PROPER ATTRIBUTION OVERLAY with clickable links */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-xs leading-relaxed">
                              Photo by{' '}
                              <a
                                href={image.authorUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="underline hover:text-purple-300 font-medium"
                              >
                                {image.author}
                              </a>
                              {' '}on{' '}
                              <a
                                href={image.unsplashUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="underline hover:text-purple-300 font-medium"
                              >
                                Unsplash
                              </a>
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Load More */}
                    {unsplashPage < unsplashTotalPages && (
                      <div className="flex justify-center mt-4">
                        <button
                          onClick={loadMoreUnsplash}
                          disabled={loading}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                        >
                          {loading ? (
                            <Loader size={18} className="animate-spin" />
                          ) : (
                            <ChevronRight size={18} />
                          )}
                          {t('common.loadMore', 'Mehr laden')}
                        </button>
                      </div>
                    )}

                    {/* ✅ UNSPLASH ATTRIBUTION FOOTER (Always visible) */}
                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                        <p className="mb-2">
                          {t('backgrounds.unsplashNote', 'Alle Bilder von Unsplash unterliegen der')}
                          {' '}
                          <a
                            href="https://unsplash.com/license"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:underline"
                          >
                            Unsplash Lizenz
                          </a>
                        </p>
                        <a
                          href="https://unsplash.com?utm_source=alpenscreen&utm_medium=referral"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 transition-colors"
                        >
                          {t('backgrounds.poweredBy', 'Powered by')} Unsplash
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-gray-500 dark:text-gray-400">
                    <Search size={48} className="mb-2 opacity-50" />
                    <p>{t('backgrounds.searchHint', 'Suche nach Bildern auf Unsplash')}</p>
                    <p className="text-sm mt-1">{t('backgrounds.searchExample', 'z.B. "Restaurant", "Natur", "Abstrakt"')}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackgroundGalleryModal;